import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import { useNavigate } from 'react-router-dom';

import etfs from '../data/etfs';
import { searchETFRecommendations } from '../services/etfService';
import { analyzeUserQuery } from '../utils/semanticAnalysis';


const POPULAR_SUGGESTIONS = [
  'Je cherche une croissance technologique avec des frais sous 0,15%',
  'Un ETF distribuant pour une approche de revenu avec des données officielles',
  'Une exposition Europe équilibrée et peu coûteuse',
];

const percent = (value, digits = 1) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return 'Non publié';
  }
  return `${Number(value).toFixed(digits)} %`;
};

function normalize(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function searchEtfs(items, query) {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return [];

  const { keywords, intent, numericCriteria } = analyzeUserQuery(trimmedQuery);
  const terms = keywords
    .map(normalize)
    .filter((term) => term && !['frais', 'rendement', 'performance', 'dividende'].includes(term))
    .filter((term) => !/[<>%\d]/.test(term));
  return items
    .filter((etf) => {
      const searchable = normalize([
        etf.name, etf.issuer, etf.region, etf.sector, etf.type, etf.replication,
      ].join(' '));
      if (numericCriteria.maxFees !== undefined && Number(etf.TER) > numericCriteria.maxFees) return false;
      if (numericCriteria.minPerformance !== undefined && Number(etf.performance) < numericCriteria.minPerformance) return false;
      if (intent !== 'all' && !searchable.includes(normalize(intent))) return false;
      return intent !== 'all' || terms.length === 0 || terms.some((term) => searchable.includes(term));
    })
    .sort((left, right) => Number(left.TER) - Number(right.TER));
}

export function buildClientFallback(query, limit = 6) {
  const matches = searchEtfs(etfs, query).slice(0, limit);
  return {
    query,
    interpretation: "Analyse locale simplifiée: le service de recherche n'a pas répondu.",
    criteria: {},
    execution: {
      mode: 'deterministic',
      provider: 'client_fallback',
      model: null,
      llm_used: false,
      fallback_reason: "Le service backend est indisponible; résultats locaux de secours.",
      data_source: 'catalogue intégré au navigateur',
      data_is_live: false,
      data_is_researched: false,
      market_data_provider: 'client_fallback',
      market_data_fallback_reason: "Le service backend est indisponible; catalogue local utilisé.",
      data_freshness: 'démonstration non actualisée',
      catalog_size: etfs.length,
    },
    disclaimer: 'Outil pédagogique, pas un conseil financier. Vérifiez chaque donnée avant toute décision.',
    results: matches.map((etf, index) => ({
      name: etf.name,
      issuer: etf.issuer,
      region: etf.region,
      sector: etf.sector,
      replication: etf.replication,
      distribution: etf.type,
      score: Math.max(50, 78 - index * 4),
      confidence: 'exploratoire',
      reasons: ['Correspondance trouvée dans le catalogue local de secours.'],
      cautions: ['Données de démonstration non actualisées.'],
      metrics: {
        price: etf.price,
        performance_1y: etf.performance,
        volatility_1y: etf.volatility,
        dividend_yield: etf.dividendYield,
        fees: etf.TER,
      },
    })),
  };
}

function ResultCard({ result, onSelect }) {
  return (
    <Card variant="outlined" className="search-result-card" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea onClick={() => onSelect(result)} sx={{ flex: 1, alignItems: 'stretch' }}>
        <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
          <Stack direction="row" justifyContent="space-between" gap={2} alignItems="flex-start">
            <Box>
              <Typography variant="overline" color="primary.main">{result.issuer} · {result.region}</Typography>
              <Typography variant="h5" component="h3">{result.name}</Typography>
            </Box>
            <Chip label={`${result.score}/100`} color="primary" size="small" />
          </Stack>

          <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap sx={{ my: 2 }}>
            <Chip label={result.sector} size="small" variant="outlined" />
            <Chip label={`Confiance ${result.confidence}`} size="small" variant="outlined" />
          </Stack>

          <LinearProgress variant="determinate" value={result.score} sx={{ mb: 2, height: 6, borderRadius: 4 }} />

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6}><Typography variant="caption">Frais</Typography><Typography fontWeight={700}>{percent(result.metrics.fees, 2)}</Typography></Grid>
            <Grid item xs={6}><Typography variant="caption">Performance indicative</Typography><Typography fontWeight={700}>{percent(result.metrics.performance_1y)}</Typography></Grid>
            <Grid item xs={6}><Typography variant="caption">Volatilité</Typography><Typography fontWeight={700}>{percent(result.metrics.volatility_1y)}</Typography></Grid>
            <Grid item xs={6}><Typography variant="caption">Dividendes</Typography><Typography fontWeight={700}>{percent(result.metrics.dividend_yield)}</Typography></Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Pourquoi cette proposition ?</Typography>
          {result.reasons.slice(0, 3).map((reason) => (
            <Typography key={reason} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>• {reason}</Typography>
          ))}
          <Typography variant="body2" color="warning.dark" sx={{ mt: 1.5 }}>
            À surveiller : {result.cautions[0]}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2.5, color: 'primary.main' }}>
            <ForumRoundedIcon fontSize="small" />
            <Typography variant="button">Ouvrir la fiche complète et discuter</Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
      {result.source_url && (
        <Box sx={{ px: { xs: 2.25, md: 3 }, pb: 2.5 }}>
          <Button
            component="a"
            href={result.source_url}
            target="_blank"
            rel="noreferrer"
            size="small"
            variant="text"
          >
            Ouvrir la source officielle
          </Button>
          {result.source_date && (
            <Typography variant="caption" color="text.secondary" display="block">
              Date indiquée par la source : {result.source_date}
            </Typography>
          )}
        </Box>
      )}
    </Card>
  );
}

export default function ETFSearcher({ title = "Décrivez l'investissement que vous recherchez" }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [networkFallback, setNetworkFallback] = useState(false);

  const runSearch = async (nextQuery = query) => {
    const cleanQuery = nextQuery.trim();
    if (!cleanQuery) return;
    setQuery(nextQuery);
    setLoading(true);
    setNetworkFallback(false);
    try {
      setReport(await searchETFRecommendations(cleanQuery, 6));
    } catch (error) {
      setNetworkFallback(true);
      setReport(buildClientFallback(cleanQuery, 6));
    } finally {
      setLoading(false);
    }
  };

  const selectEtf = (result) => {
    const identifier = result.symbol || result.isin || result.name;
    navigate(`/etf/${encodeURIComponent(identifier)}`, {
      state: { originalQuery: report?.query || query },
    });
  };

  return (
    <Box className="search-report" sx={{ width: '100%' }}>
      <Box sx={{ maxWidth: 860, mx: 'auto', textAlign: 'center', mb: 4 }}>
        <Chip icon={<AutoAwesomeRoundedIcon />} label="Recherche en langage naturel" color="primary" sx={{ mb: 2 }} />
        <Typography variant="h2" component="h1" gutterBottom>{title}</Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
          Parlez simplement de votre objectif, de votre horizon ou du niveau de risque souhaité.
          ZoomETF transforme votre demande en critères vérifiables puis explique chaque proposition.
        </Typography>
      </Box>

      <Box className="no-print" sx={{ maxWidth: 920, mx: 'auto' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Votre besoin"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                runSearch();
              }
            }}
            placeholder="Ex. Je cherche un ETF européen prudent, peu coûteux, orienté revenu..."
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment>,
            }}
          />
          <Button
            variant="contained"
            size="large"
            disabled={!query.trim() || loading}
            onClick={() => runSearch()}
            sx={{ minWidth: 150 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Analyser'}
          </Button>
        </Stack>

        <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          {POPULAR_SUGGESTIONS.map((suggestion) => (
            <Chip key={suggestion} label={suggestion} variant="outlined" onClick={() => runSearch(suggestion)} />
          ))}
        </Stack>
      </Box>

      {report && (
        <Box sx={{ mt: 5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h4" component="h2">Propositions expliquées</Typography>
              <Typography color="text.secondary">{report.interpretation}</Typography>
            </Box>
            <Button
              className="no-print"
              variant="outlined"
              startIcon={<PictureAsPdfRoundedIcon />}
              onClick={() => window.print()}
            >
              Enregistrer en PDF
            </Button>
          </Stack>

          {networkFallback && <Alert severity="warning" sx={{ mb: 2 }}>Le service est indisponible. Résultats locaux de secours affichés.</Alert>}
          {!report.execution.llm_used && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Mode actuel : analyse structurée déterministe. Aucun résultat n’est présenté comme généré par un LLM.
              {report.execution.fallback_reason ? ` ${report.execution.fallback_reason}` : ''}
            </Alert>
          )}
          <Alert
            severity={report.execution.data_is_live ? 'success' : report.execution.data_is_researched ? 'info' : 'warning'}
            sx={{ mb: 3 }}
          >
            Source : {report.execution.data_source}.{' '}
            {report.execution.data_is_live
              ? 'Données externes connectées.'
              : report.execution.data_is_researched
                ? 'Recherche externe sourcée, non temps réel.'
                : 'Données de démonstration, non temps réel.'}
            {report.execution.data_freshness ? ` Fraîcheur : ${report.execution.data_freshness}.` : ''}
            {report.execution.market_data_fallback_reason ? ` ${report.execution.market_data_fallback_reason}` : ''}
          </Alert>

          {report.results.length === 0 ? (
            <Alert severity="warning">Aucune proposition ne respecte tous les critères. Reformulez ou assouplissez une contrainte.</Alert>
          ) : (
            <Grid container spacing={2.5}>
              {report.results.map((result) => (
                <Grid item xs={12} md={6} key={result.symbol || result.isin || result.name}>
                  <ResultCard result={result} onSelect={selectEtf} />
                </Grid>
              ))}
            </Grid>
          )}

          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 3 }}>
            {report.disclaimer}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
